import { Link } from "react-router-dom";
import Card from "components/DesignSystem/Card/Card";
import { Button } from "components/DesignSystem/Button/Button";
import { Divider } from "components/design/Divider";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { ArrowUpRight12 } from "components/icons/ArrowUpRight12";
import { Hourglass } from "components/icons/Hourglass";
import { Info } from "components/icons/Info";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { QuarterlyBreakdown as QuarterlyBreakdownType } from "types/Models/estimatedTax";
import { focusChatInput } from "util/focusChatInput";
import { Banner } from "components/DesignSystem/Banner/Banner";

export type EstimatedTaxData = {
  enrolled: boolean;
  has_financials: boolean;
  estimated_payment_for_quarter: number;
  total_taxable_income: number;
  annual_federal_tax: number;
  paid_ytd: number;
  remaining_due: number;
  federal_tax_rate: number;
  current_quarter: QuarterlyBreakdownType;
};

type Props = {
  data: EstimatedTaxData;
  entityId: string;
  groupId: string;
  season: string;
};

export const EstimatedTaxField = ({
  data,
  entityId,
  groupId,
  season,
}: Props) => {
  const { isAdmin, isCpa } = useRoleBasedView();
  const isServiceUser = isAdmin || isCpa;

  const fullDetailsLink = isServiceUser
    ? `/admin/crm/${groupId}/advance-tax?entity=${entityId}&season=${season}`
    : `/tax/advance-tax?entity=${entityId}`;

  if (!data?.has_financials) {
    return (
      <Banner
        type="neutral"
        title=" Your estimated tax will be available once your financials are added"
        description="We need your income statement to calculate your estimated tax and show you what's due."
      />
    );
  }

  if (!data?.enrolled || !data.current_quarter) {
    return (
      <div className="t-flex t-items-start t-gap-3 t-p-4 t-border t-border-solid t-border-blue-10 t-bg-blue-0 t-rounded-lg">
        <Hourglass size={20} />
        <div className="t-flex t-flex-col t-gap-2">
          <p className="t-text-subtext-sm t-font-medium t-text-text-100 t-m-0">
            Your estimated tax is being calculated
          </p>
          <p className="t-text-body-sm t-text-text-30 t-m-0">
            We are processing your financials now. Once the calculation is
            complete, your final numbers will appear here.
          </p>
          <div>
            <Button
              type="button"
              customType="secondary"
              onClick={focusChatInput}
            >
              Talk to the team
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card.Root borderRounded="lg" className="!t-p-0">
      <Card.Body className="!t-mt-0 t-p-6 t-flex t-flex-col t-gap-4">
        <div className="t-space-y-1 t-mb-2">
          <p className="t-text-body-sm t-text-text-30 t-m-0">
            Estimated payment for this quarter
          </p>
          <p className="t-text-subtext-lg t-font-semibold t-text-text-80 t-m-0">
            <AmountSuperScript amount={data.estimated_payment_for_quarter} />
          </p>
        </div>

        <div className="t-flex t-items-stretch t-gap-6">
          <div className="t-flex-1">
            <p className="t-text-body-sm t-text-text-30 t-m-0">
              Total taxable income
            </p>
            <p className="t-text-body-sm t-text-text-60 t-m-0">
              <AmountSuperScript amount={data.total_taxable_income} />
            </p>
          </div>
          <Divider orientation="vertical" type="dashed" />
          <div className="t-flex-1">
            <p className="t-text-body-sm t-text-text-30 t-m-0">
              Annual federal tax
            </p>
            <p className="t-text-body-sm t-text-text-60 t-m-0">
              <AmountSuperScript amount={data.annual_federal_tax} />
            </p>
          </div>
          <Divider orientation="vertical" type="dashed" />
          <div className="t-flex-1">
            <p className="t-text-body-sm t-text-text-30 t-m-0">
              Paid year to date
            </p>
            <p className="t-text-body-sm t-text-text-60 t-m-0">
              <AmountSuperScript amount={data.paid_ytd} />
            </p>
          </div>
        </div>

        <Link
          to={fullDetailsLink}
          target="_blank"
          rel="noopener noreferrer"
          className="t-flex t-items-center t-gap-1 t-text-body-sm t-text-text-30 t-no-underline hover:t-underline"
        >
          View full details
          <ArrowUpRight12 strokeWidth="1" />
        </Link>
      </Card.Body>
    </Card.Root>
  );
};
